import * as Yup from 'yup';
import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import {
    Card,
    Stack,
    Container,
    Typography,
    TextField,
    IconButton,
    InputAdornment,
    Alert,
    Box,
    Link,
    Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
import Page from 'src/components/Page';
import syndicationApi from 'src/utils/syndicationApi';

const RootStyle = styled(Page)(({ theme }) => ({
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    padding: theme.spacing(4, 0),
}));

const FormCard = styled(Card)(({ theme }) => ({
    maxWidth: 480,
    width: '100%',
    padding: theme.spacing(5),
    borderRadius: 16,
    boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
}));

export default function InvestorLogin() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1 = email+password, 2 = TOTP
    const [tempToken, setTempToken] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [apiError, setApiError] = useState('');
    const [apiInfo, setApiInfo] = useState('');
    const [unverifiedEmail, setUnverifiedEmail] = useState(''); // Track email for resend link

    // Step 1: Email + Password
    const Step1Schema = Yup.object().shape({
        email: Yup.string().email('Must be a valid email').required('Email is required'),
        password: Yup.string().required('Password is required'),
    });

    const step1Formik = useFormik({
        initialValues: { email: '', password: '' },
        validationSchema: Step1Schema,
        onSubmit: async (values, { setSubmitting }) => {
            setApiError('');
            setApiInfo('');
            try {
                const response = await syndicationApi.post('investor/login/', values);
                const data = response.data;
                if (data.data?.requires_2fa) {
                    setTempToken(data.data.temp_token);
                    setStep(2);
                }
            } catch (error) {
                const resp = error.response?.data;
                const code = resp?.code;
                if (code === 'EMAIL_NOT_VERIFIED') {
                    setApiInfo(resp?.message);
                    setUnverifiedEmail(values.email); // Store email for resend link
                } else if (code === 'PENDING_APPROVAL') {
                    setApiInfo(resp?.message);
                } else if (code === '2FA_REQUIRED') {
                    if (resp?.setup_token) {
                        navigate(`/investor/setup-2fa?setup_token=${resp.setup_token}`);
                        return;
                    }
                    setApiInfo(resp?.message);
                } else if (code === 'SUSPENDED' || code === 'REJECTED') {
                    setApiError(resp?.message);
                } else if (code === 'RATE_LIMITED') {
                    setApiError(resp?.message);
                } else {
                    setApiError(resp?.message || 'Login failed. Please try again.');
                }
            } finally {
                setSubmitting(false);
            }
        },
    });

    // Step 2: TOTP code
    const Step2Schema = Yup.object().shape({
        totp_code: Yup.string()
            .matches(/^[0-9]{6}$/, 'Must be a 6-digit code')
            .required('2FA code is required'),
    });

    const step2Formik = useFormik({
        initialValues: { totp_code: '' },
        validationSchema: Step2Schema,
        onSubmit: async (values, { setSubmitting }) => {
            setApiError('');
            try {
                const response = await syndicationApi.post('investor/login/2fa/', {
                    temp_token: tempToken,
                    totp_code: values.totp_code,
                });
                const data = response.data;
                if (data.data?.key) {
                    // Store investor auth data
                    localStorage.setItem('investorAuthToken', data.data.key);
                    localStorage.setItem('investorUser', JSON.stringify(data.data.user));
                    localStorage.setItem('activeRole', 'INVESTOR');

                    // Redirect to investor dashboard
                    navigate('/investor/dashboard', { replace: true });
                }
            } catch (error) {
                const resp = error.response?.data;
                if (resp?.code === 'INVALID_TEMP_TOKEN') {
                    setApiError('Session expired. Please log in again.');
                    setStep(1);
                    setTempToken('');
                } else if (resp?.code === 'TOTP_RATE_LIMITED') {
                    setApiError('Too many failed attempts. Please log in again.');
                    setStep(1);
                    setTempToken('');
                } else {
                    setApiError(resp?.message || 'Invalid 2FA code.');
                }
            } finally {
                setSubmitting(false);
            }
        },
    });

    return (
        <RootStyle title="Investor Login | Gateway X Syndication">
            <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center' }}>
                <FormCard>
                    <Stack spacing={3}>
                        <Box sx={{ textAlign: 'center', mb: 1 }}>
                            <Typography variant="h4" gutterBottom>Investor Login</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Gateway X Syndication Platform
                            </Typography>
                        </Box>

                        {apiError && (
                            <Alert severity="error" sx={{ mb: 1 }}>
                                {apiError}
                            </Alert>
                        )}

                        {apiInfo && (
                            <Alert severity="info" sx={{ mb: 1 }}>
                                {apiInfo}
                                {unverifiedEmail && (
                                    <Box component="span" sx={{ display: 'block', mt: 1 }}>
                                        <Link
                                            component={RouterLink}
                                            to={`/investor/resend-verification?email=${encodeURIComponent(unverifiedEmail)}`}
                                            variant="body2"
                                            underline="hover"
                                        >
                                            Click here to resend verification email
                                        </Link>
                                    </Box>
                                )}
                            </Alert>
                        )}

                        {/* Step 1: Email + Password */}
                        {step === 1 && (
                            <FormikProvider value={step1Formik}>
                                <Form autoComplete="off" noValidate onSubmit={step1Formik.handleSubmit}>
                                    <Stack spacing={2.5}>
                                        <TextField
                                            fullWidth
                                            label="Email Address"
                                            type="email"
                                            autoComplete="email"
                                            {...step1Formik.getFieldProps('email')}
                                            error={Boolean(step1Formik.touched.email && step1Formik.errors.email)}
                                            helperText={step1Formik.touched.email && step1Formik.errors.email}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Password"
                                            type={showPassword ? 'text' : 'password'}
                                            autoComplete="current-password"
                                            {...step1Formik.getFieldProps('password')}
                                            error={Boolean(step1Formik.touched.password && step1Formik.errors.password)}
                                            helperText={step1Formik.touched.password && step1Formik.errors.password}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                            <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <Box sx={{ textAlign: 'right' }}>
                                            <Link component={RouterLink} to="/investor/forgot-password" variant="body2">
                                                Forgot password?
                                            </Link>
                                        </Box>
                                        <LoadingButton
                                            fullWidth
                                            size="large"
                                            type="submit"
                                            variant="contained"
                                            loading={step1Formik.isSubmitting}
                                        >
                                            Continue
                                        </LoadingButton>
                                    </Stack>
                                </Form>
                            </FormikProvider>
                        )}

                        {/* Step 2: TOTP */}
                        {step === 2 && (
                            <FormikProvider value={step2Formik}>
                                <Form autoComplete="off" noValidate onSubmit={step2Formik.handleSubmit}>
                                    <Stack spacing={2.5}>
                                        <Alert severity="info" sx={{ mb: 1 }}>
                                            Enter the 6-digit code from your authenticator app.
                                        </Alert>
                                        <TextField
                                            fullWidth
                                            label="2FA Code"
                                            placeholder="000000"
                                            inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: 24, letterSpacing: 8 } }}
                                            {...step2Formik.getFieldProps('totp_code')}
                                            error={Boolean(step2Formik.touched.totp_code && step2Formik.errors.totp_code)}
                                            helperText={step2Formik.touched.totp_code && step2Formik.errors.totp_code}
                                        />
                                        <LoadingButton
                                            fullWidth
                                            size="large"
                                            type="submit"
                                            variant="contained"
                                            loading={step2Formik.isSubmitting}
                                        >
                                            Verify & Login
                                        </LoadingButton>
                                        <LoadingButton
                                            fullWidth
                                            size="large"
                                            variant="text"
                                            onClick={() => { setStep(1); setTempToken(''); setApiError(''); }}
                                        >
                                            Back to Login
                                        </LoadingButton>
                                    </Stack>
                                </Form>
                            </FormikProvider>
                        )}

                        <Divider />
                        <Typography variant="body2" align="center" color="text.secondary">
                            Don't have an account?{' '}
                            <Link component={RouterLink} to="/investor/register" variant="subtitle2">
                                Register
                            </Link>
                        </Typography>
                    </Stack>
                </FormCard>
            </Container>
        </RootStyle>
    );
}
